import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import { Loader, routes, SentryErrorBoundary } from '#shared';

import { DataProvider } from './DataProvider';
import { RouteGuard } from './RouteGuard';

// Lazy load page components for code splitting

const StartPage = lazy(() =>
  import('#pages/StartPage').then(module => ({
    default: module.StartPage,
  })),
);

const MainMapPage = lazy(() =>
  import('#pages/MainMapPage').then(module => ({
    default: module.MainMapPage,
  })),
);

const NewCasesMapPage = lazy(() =>
  import('#pages/NewCasesMapPage').then(module => ({
    default: module.NewCasesMapPage,
  })),
);

const ErrorsPage = lazy(() =>
  import('#pages/ErrorsPage').then(module => ({
    default: module.ErrorsPage,
  })),
);

const StatisticsPage = lazy(() =>
  import('#pages/StatisticsPage').then(module => ({
    default: module.StatisticsPage,
  })),
);

const HubsReportPage = lazy(() =>
  import('#pages/HubsReportPage').then(module => ({
    default: module.HubsReportPage,
  })),
);

// TMDXNC-68 — one hub's big-screen dashboard, keyed by a route parameter.
const HubDashboardPage = lazy(() =>
  import('#pages/HubDashboardPage').then(module => ({
    default: module.HubDashboardPage,
  })),
);

const StatsPage = lazy(() =>
  import('#pages/StatsPage').then(module => ({
    default: module.StatsPage,
  })),
);

const SettingsPage = lazy(() =>
  import('#pages/SettingsPage').then(module => ({
    default: module.SettingsPage,
  })),
);

const MySettingsPage = lazy(() =>
  import('#pages/MySettingsPage').then(module => ({
    default: module.MySettingsPage,
  })),
);

// TMDXNC-241 — the admin panel (v1: user list with live online status).
const AdminPage = lazy(() =>
  import('#pages/AdminPage').then(module => ({
    default: module.AdminPage,
  })),
);

const LogoutPage = lazy(() =>
  import('#pages/LogoutPage').then(module => ({
    default: module.LogoutPage,
  })),
);

const NoAccessPage = lazy(() =>
  import('#pages/NoAccessPage').then(module => ({
    default: module.NoAccessPage,
  })),
);

const PageNotFound = lazy(() =>
  import('#pages/PageNotFound').then(module => ({
    default: module.PageNotFound,
  })),
);

const CasesTimelinePage = lazy(() =>
  import('#pages/CasesTimelinePage').then(module => ({
    default: module.CasesTimelinePage,
  })),
);

// Ported from cc3-event-poc.
const AlertsDashboardPage = lazy(() =>
  import('#pages/AlertsDashboardPage').then(module => ({
    default: module.AlertsDashboardPage,
  })),
);

// TMDXNC-65 — Aviation Timeline v3 rebuild.
const AviationTimelinePage = lazy(() =>
  import('#pages/AviationTimelinePage').then(module => ({
    default: module.AviationTimelinePage,
  })),
);

export const AppRouter = () => (
  // SpecRef: ARCH-005
  // Reference: docs/decisions/ARCH-005-ui-error-detection-resilience.md#1-sentryerrorboundary-placement
  // Router-level boundary isolates page crashes from the outer shell.
  <SentryErrorBoundary showDialog={false}>
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          path={routes.START_PAGE}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <StartPage />
            </RouteGuard>
          }
        />
        <Route
          path={routes.MAIN_MAP}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <MainMapPage />
            </RouteGuard>
          }
        />
        <Route
          path={routes.NEW_CASES_MAP}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <NewCasesMapPage />
            </RouteGuard>
          }
        />
        <Route
          path={routes.ERRORS}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <ErrorsPage />
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.STATISTICS}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <StatisticsPage />
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.HUBS_REPORT}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <HubsReportPage />
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.HUB_DASHBOARD}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              {/* `DataProvider` matters here rather than being boilerplate: the
                  hubs resource report is one of the live queries it anchors, and
                  this page reads a single hub out of it. */}
              <DataProvider>
                <HubDashboardPage />
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.SETTINGS}
          element={
            <RouteGuard ssoUserGroups={['Admin']}>
              <DataProvider>
                <SettingsPage />
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.MY_SETTINGS}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter', 'Statistics']}>
              <MySettingsPage />
            </RouteGuard>
          }
        />
        <Route
          path={routes.ADMIN}
          element={
            <RouteGuard ssoUserGroups={['Admin']}>
              <AdminPage />
            </RouteGuard>
          }
        />
        <Route
          path={routes.STATS}
          element={
            <RouteGuard ssoUserGroups={['Statistics']}>
              <StatsPage />
            </RouteGuard>
          }
        />
        <Route
          path={routes.CASE_TIMELINE}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <CasesTimelinePage />
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.ALERTS_DASHBOARD}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <AlertsDashboardPage />
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.AVIATION_TIMELINE}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <AviationTimelinePage />
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.LOGOUT}
          element={
            <RouteGuard>
              <LogoutPage />
            </RouteGuard>
          }
        />
        <Route
          path={routes.NO_ACCESS}
          element={
            <RouteGuard>
              <NoAccessPage />
            </RouteGuard>
          }
        />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  </SentryErrorBoundary>
);
